/**
 * HostManagementPipelineTab (Iter 9.47.r refresh)
 *
 * Per user spec: "show the actual host dashboards listed like they would be on
 * the /me dashboard hosting. with the host brand name and the dashboards nested
 * under each. the copy button for the host user /me magic link and the copy
 * button for the host dashboard magic link for the incognito testing.. the
 * entire intakes etc should show everything that is attached to those users
 * and dashboard data, the intakes for what they filled out"
 *
 * Renders one row per host (grouped by host_user_id), with their dashboards
 * nested underneath and per-dashboard copy buttons for:
 *  (a) /me magic link (user impersonation)
 *  (b) dashboard magic link (direct redirect to /host/v2/<dash_id>)
 *
 * Each row also exposes the intake form data the host filled in.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Loader2, ChevronDown, ChevronRight,
  UserPlus, ExternalLink, FileText, Mail,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import CopyButton from "@/components/CopyButton";
import UniversalInviteModal from "@/components/host/UniversalInviteModal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_BASE = process.env.REACT_APP_BACKEND_URL;

function buildMagicUrl(token, redirect) {
  if (!token) return null;
  const base = `${FRONTEND_BASE}/auth/magic?token=${token}`;
  return redirect ? `${base}&redirect=${encodeURIComponent(redirect)}` : base;
}

async function fetchUserMagicLink(userId, redirect) {
  const sessionToken = localStorage.getItem("session_token");
  const r = await fetch(`${API}/admin/users/${userId}/magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
  });
  if (!r.ok) throw new Error(`Magic link fetch failed (${r.status})`);
  const data = await r.json();
  return buildMagicUrl(data.token, redirect);
}

function IntakeDetailsModal({ open, onOpenChange, host }) {
  if (!host) return null;
  const intake = host.intake_responses || host.intake || {};
  const entries = Object.entries(intake);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="intake-details-modal">
        <DialogHeader>
          <DialogTitle>{host.brand_name || host.name} - intake</DialogTitle>
          <DialogDescription>Everything {host.first_name || "this host"} filled in during onboarding.</DialogDescription>
        </DialogHeader>
        {entries.length === 0 ? (
          <p className="text-sm text-stone-500 py-6 text-center">No intake data captured yet.</p>
        ) : (
          <div className="space-y-3 mt-2">
            {entries.map(([k, v]) => (
              <div key={k} className="border-b border-stone-100 pb-2 last:border-0">
                <p className="text-[10px] uppercase tracking-wide text-stone-500">{k.replace(/_/g, " ")}</p>
                <p className="text-sm text-stone-900 break-words">{
                  Array.isArray(v) ? v.join(", ") :
                  typeof v === "object" ? JSON.stringify(v, null, 2) :
                  String(v ?? "-")
                }</p>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function HostManagementPipelineTab({ event }) {
  const category = event?.category || event?.pilot_category;
  const [hostsByUser, setHostsByUser] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [intakeViewing, setIntakeViewing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!category) return;
    let alive = true;
    (async () => {
      const token = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const r = await fetch(`${API}/admin/host-applications?category=${category}`, { headers });
        const data = r.ok ? await r.json() : { applications: [] };
        const apps = data.applications || [];

        // Group by host_user_id (or email if no user yet)
        const byUser = new Map();
        for (const a of apps) {
          const key = a.user_id || a.host_user_id || a.email || `app_${a.application_id}`;
          if (!byUser.has(key)) {
            byUser.set(key, {
              user_id: a.user_id || a.host_user_id || null,
              email: a.email,
              first_name: a.first_name,
              brand_name: a.brand_name || a.host_name || a.name,
              dashboards: [],
              intake_responses: a.intake_responses || a.intake || {},
              status: a.status,
              pilot: a.pilot || a.pilot_tier || a.is_pilot,
              city: a.city,
            });
          }
          const h = byUser.get(key);
          // Each application carries a dashboard reference (event_id)
          if (a.event_id || a.dashboard_id) {
            h.dashboards.push({
              event_id: a.event_id || a.dashboard_id,
              name: a.dashboard_name || a.event_name || h.brand_name,
              status: a.dashboard_status || a.status,
              subtype: a.subtype,
            });
          }
          // Merge most-recent intake into row
          if (a.intake_responses && Object.keys(a.intake_responses).length) {
            h.intake_responses = a.intake_responses;
          }
        }
        if (alive) setHostsByUser(Array.from(byUser.values()));
      } catch {
        if (alive) setHostsByUser([]);
      }
    })();
    return () => { alive = false; };
  }, [category, refreshKey]);

  const counts = useMemo(() => {
    if (!hostsByUser) return null;
    let waitlist = 0, pilots = 0, subs = 0, live = 0;
    for (const h of hostsByUser) {
      if (h.status === "waitlist") waitlist += 1;
      else if (h.pilot) pilots += 1;
      else if (h.status === "active") subs += 1;
      if (h.dashboards.some((d) => d.status === "active")) live += 1;
    }
    return { waitlist, pilots, subs, live };
  }, [hostsByUser]);

  if (!hostsByUser) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 text-sm" data-testid="pipeline-loading">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading pipeline...
      </div>
    );
  }

  return (
    <div data-testid="host-management-pipeline" className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1 capitalize">{category} pipeline</h2>
          <p className="text-xs text-gray-500">Every host with a dashboard or application in this category.</p>
        </div>
        <Button
          size="sm"
          onClick={() => setInviteOpen(true)}
          className="bg-stone-900 hover:bg-stone-800 text-white shrink-0"
          data-testid="pipeline-invite-btn"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" />
          Invite
        </Button>
      </div>

      {/* Summary chips */}
      {counts && (
        <div className="flex gap-2 flex-wrap text-[10px]">
          <Badge variant="secondary" data-testid="pipeline-count-waitlist">Waitlist {counts.waitlist}</Badge>
          <Badge variant="secondary" data-testid="pipeline-count-pilots">Pilots {counts.pilots}</Badge>
          <Badge variant="secondary" data-testid="pipeline-count-subscribers">Subscribers {counts.subs}</Badge>
          <Badge variant="secondary" data-testid="pipeline-count-live">Live {counts.live}</Badge>
        </div>
      )}

      {/* Empty state */}
      {hostsByUser.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500">No hosts in this category yet. Click <strong>Invite</strong> to send the first one.</p>
        </div>
      )}

      {/* Host rows */}
      <div className="space-y-2">
        {hostsByUser.map((h, i) => {
          const id = h.user_id || h.email || `idx-${i}`;
          const isOpen = expanded[id] !== false; // open by default
          const intakeCount = Object.keys(h.intake_responses || {}).length;
          return (
            <Card key={id} data-testid={`pipeline-host-${id}`} className="overflow-hidden">
              <CardContent className="p-0">
                {/* Host header row */}
                <div className="flex items-center gap-2 p-3 border-b border-stone-100">
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => ({ ...s, [id]: !isOpen }))}
                    className="text-stone-400 hover:text-stone-700"
                    data-testid={`pipeline-host-toggle-${id}`}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-stone-900 truncate">{h.brand_name || h.email || "Unnamed"}</p>
                      {h.pilot && <Badge variant="default" className="text-[9px] py-0">Pilot</Badge>}
                      {h.status && <Badge variant="outline" className="text-[9px] py-0 capitalize">{h.status}</Badge>}
                    </div>
                    <p className="text-[10px] text-stone-500 truncate">
                      {[h.first_name, h.email, h.city].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {h.user_id && (
                    <CopyButton
                      getValue={() => fetchUserMagicLink(h.user_id, "/me")}
                      label="/me link"
                      successMessage="/me magic link copied"
                      testId={`copy-me-link-${id}`}
                    />
                  )}
                  {intakeCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIntakeViewing(h)}
                      className="text-[10px] h-7 px-2 gap-1"
                      data-testid={`pipeline-intake-btn-${id}`}
                      title="View intake responses"
                    >
                      <FileText className="w-3 h-3" />
                      Intake
                    </Button>
                  )}
                </div>

                {/* Nested dashboards */}
                {isOpen && h.dashboards.length > 0 && (
                  <div className="bg-stone-50/40">
                    {h.dashboards.map((d) => (
                      <div
                        key={d.event_id}
                        data-testid={`pipeline-dashboard-${d.event_id}`}
                        className="flex items-center gap-2 px-3 py-2 border-b border-stone-100 last:border-0 pl-10"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-stone-800 truncate">{d.name}</p>
                          <p className="text-[10px] text-stone-500 capitalize">
                            {d.subtype ? `${d.subtype} · ` : ""}{d.status || "draft"}
                          </p>
                        </div>
                        {h.user_id && (
                          <CopyButton
                            getValue={() => fetchUserMagicLink(h.user_id, `/host/v2/${d.event_id}`)}
                            label="dashboard link"
                            successMessage="Dashboard magic link copied"
                            testId={`copy-dashboard-link-${d.event_id}`}
                          />
                        )}
                        <a
                          href={`/host/v2/${d.event_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-stone-400 hover:text-stone-700"
                          data-testid={`pipeline-dashboard-open-${d.event_id}`}
                          title="Open dashboard"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {/* No dashboard yet (waitlist) */}
                {isOpen && h.dashboards.length === 0 && (
                  <div className="bg-stone-50/40 px-3 py-2 pl-10">
                    <p className="text-[10px] text-stone-500 italic flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      No dashboard yet - send invite to start onboarding.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <UniversalInviteModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        category={category}
        onInviteSent={() => setRefreshKey((k) => k + 1)}
      />
      <IntakeDetailsModal
        open={!!intakeViewing}
        onOpenChange={(o) => !o && setIntakeViewing(null)}
        host={intakeViewing}
      />
    </div>
  );
}
